import { BehaviorSubject, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  BackpressureQueueCommand,
  BackpressureQueueWorker,
  ProcessQueueCommand,
  SetPeriodCommand,
  SetQueueCountCommand,
} from './backpressure-queue-shared';

/**
 * Controls the flow of data being processed.
 */
export class BackpressureQueue<T> {
  private _queue: T[] = [];
  private _controller = new BehaviorSubject({ processing: false });
  private _executeProcessor: () => void;
  private worker: Worker;

  /**
   * Number of items remaining in the queue
   */
  public get queueCount() {
    return this._queue.length;
  }

  /**
   * @param period - Period of time between emitting the next item in the queue.
   * @param next - Called at each interval period
   */
  constructor(public period: number, public next: (item: T) => void) {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('./backpressure-queue.worker', import.meta.url), {
        type: 'module',
      });
      this.worker.onmessage = ({ data }: MessageEvent<BackpressureQueueCommand>) => {
        if (data.cmd === BackpressureQueueWorker.ProcessNextItem) {
          const msg = this._queue.shift();
          if (this.queueCount === 0) {
            this.worker.postMessage(new SetQueueCountCommand(this.queueCount));
          }
          if (msg != null) {
            this.next(msg);
          }
        }
      };

      this.worker.postMessage(new SetPeriodCommand(this.period));
      this._executeProcessor = () => {
        this.worker.postMessage(new SetQueueCountCommand(this.queueCount));
        this.worker.postMessage(new ProcessQueueCommand());
      };
    } else {
      // Worker is not supported, so process the queue locally.
      this._executeProcessor = () => this._processQueue();
    }
  }

  /**
   * Add item(s) to queue
   */
  public add(...items: T[]) {
    this._queue.push(...items);
    this._executeProcessor();
  }

  /**
   * Cleanup
   */
  public dispose() {
    this.worker?.terminate();
  }

  private _processQueue() {
    if (this._controller.value.processing) {
      return;
    }
    this._controller.next({ processing: true });
    timer(0, this.period)
      .pipe(takeUntil(this._controller.pipe(filter((c) => !c.processing))))
      .subscribe(() => {
        const msg = this._queue.shift();
        if (msg != null) {
          this.next(msg);
          if (this._queue.length === 0) {
            this._controller.next({ processing: false });
          }
        }
      });
  }
}
